import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth-lib';
import { Button, Input, Modal } from '../design-system';
import AuthLayout, { AuthPasswordInput, AuthTypeToggle } from '../components/AuthLayout';
import { ADMIN_BRAND } from '../components/authBrand';
import { TotpScanStep, TotpRecoveryCodesStep } from '../components/TotpSetupSteps';
import { sanitizeOtp, isCompleteOtp, OTP_LENGTH } from '../lib/otpInput';

const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:4000';

// Multi-tenant signup: registering here founds a NEW firm — a fresh
// Organization with this person as its first Admin. Joining an EXISTING firm
// never happens here; that is always an invite from that firm's admin on the
// Users page. The auth-service enforces the same rule server-side — so the
// Employee tab shows that invite-only notice rather than a self-signup form.
//
// ⚠️ An email that already runs another firm is fine — one address may hold one
// account PER FIRM (User's compound unique index), which is how a CA who is a
// partner at one practice and an auditor at another actually works.
function Register() {
  // form → prompt (2FA offered) → scan → recovery. The last three only happen
  // to a firm that has already been created and signed in.
  const [step, setStep] = useState('form');
  const [mode, setMode] = useState('firm'); // 'firm' → found a firm | 'employee' → invite-only notice
  const [form, setForm] = useState({ firmName: '', name: '', email: '', phone: '', password: '', confirm: '', otp: '' });
  // The firm is created only after the email answers with its code, so an
  // abandoned signup leaves no half-made organisation behind.
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [setup, setSetup] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, startTotpSetup, confirmTotpSetup } = useAuth();
  const navigate = useNavigate();

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Where a new firm goes once the 2FA detour is over, taken or not: it holds no
  // subscription yet, so the cockpit would bounce it straight back (see the
  // comment on handleSubmit).
  const goOnwards = () => navigate('/subscription');

  const sendOtp = async () => {
    setError('');
    if (!form.email.trim()) {
      setError('Enter your email first');
      return;
    }
    setOtpSending(true);
    try {
      await axios.post(`${AUTH_SERVICE_URL}/auth/register/request-otp`, { email: form.email });
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the code — try again');
    } finally {
      setOtpSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register({
        firmName: form.firmName,
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        password: form.password,
        otp: form.otp,
      });
      // ─── The 2FA offer ────────────────────────────────────────────────────
      //
      // The registrant is an Admin, and mfaRequiredFor() is true for level ≥ 4
      // from this moment — so an authenticator is not optional for them, only
      // DEFERRED: the server will stop them at the login screen and make them
      // enrol next time. Offering it here, while they are already signed in and
      // still at their desk, is the difference between doing it now and being
      // ambushed by it on the morning they first try to work.
      //
      // ⚠️ Offered, not forced. Registration completing at the QR screen would
      // leave a firm with an organization and a login nobody has finished — the
      // reason the server does not force enrolment during register either. The
      // 402 gate is untouched by this in both directions.
      setStep('prompt');
    } catch (err) {
      setError(err.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const beginTotp = async () => {
    setError('');
    setLoading(true);
    try {
      // No mfaToken: they hold a real access token from register, so the server
      // identifies them from it (totpController#resolveEnroller).
      setSetup(await startTotpSetup());
      setStep('scan');
    } catch (err) {
      setError(err.error || 'Could not start the setup — you can turn it on later from Settings.');
      setStep('prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async (typedCode) => {
    setError('');
    setLoading(true);
    try {
      const result = await confirmTotpSetup({ code: typedCode });
      setRecoveryCodes(result.recoveryCodes || []);
      setStep('recovery');
    } catch (err) {
      setError(err.error || 'That code was not accepted.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'scan') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Set up two-factor authentication"
        subtitle="Scan the code with your authenticator app"
      >
        <TotpScanStep
          setup={setup}
          onConfirm={handleConfirmSetup}
          loading={loading}
          error={error}
          // Backing out here leaves the firm made and signed in — the account is
          // in exactly the state Cancel on the prompt would have left it, since
          // enrolment does not switch 2FA on until a code proves it.
          onCancel={goOnwards}
        />
      </AuthLayout>
    );
  }

  if (step === 'recovery') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Save your recovery codes"
        subtitle="Two-factor authentication is on"
      >
        <TotpRecoveryCodesStep
          codes={recoveryCodes}
          doneLabel="Choose your plan"
          onDone={goOnwards}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      {...ADMIN_BRAND}
      title={mode === 'firm' ? 'Register your firm' : 'Joining a firm'}
      subtitle={mode === 'firm' ? 'Create a new firm workspace — you become its Admin' : 'How employees get access'}
      error={mode === 'firm' ? error : ''}
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      {/* ⚠️ Not dismissable by clicking away or by Escape — both routes out of
          this dialog are decisions the person should make on purpose, and a
          stray click landing them on the plans screen would leave them assuming
          they had declined something they never read. */}
      <Modal
        isOpen={step === 'prompt'}
        onClose={() => {}}
        title="Protect your firm with two-factor authentication"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={goOnwards} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={beginTotp} loading={loading}>
              OK
            </Button>
          </>
        }
      >
        <p>
          <strong>{form.firmName || 'Your firm'}</strong> is ready. As its Admin you can see
          everything in it — every client, every filing and every invoice — so your account is
          the one worth protecting.
        </p>
        <p>
          Press <strong>OK</strong> to set up an authenticator app now (about a minute), or{' '}
          <strong>Cancel</strong> to carry on. Your role requires one either way, so you will be
          asked again the next time you sign in.
        </p>
        {error && <p className="auth-modal-error" role="alert">{error}</p>}
      </Modal>

      <AuthTypeToggle value={mode} onChange={setMode} />

      {mode === 'employee' ? (
        <div className="auth-info-panel">
          <p><strong>Employees don't self-register.</strong></p>
          <p>
            To keep each firm's workspace secure, staff accounts are created by the firm's
            admin from their <strong>Users</strong> page. Once added, you'll receive a login
            and can sign in from the Employee tab.
          </p>
          <p>
            Already work for another firm here? Use the <strong>same email</strong> — one address
            can hold an account at as many firms as you work for, and you pick which one when you
            sign in.
          </p>
          <p>Ask your firm's admin to add you, then <Link to="/login">head to sign in</Link>.</p>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="auth-form">
        <Input
          label="Firm name"
          id="reg-firm"
          placeholder="e.g. AKPR & Associates"
          value={form.firmName}
          onChange={set('firmName')}
          required
          disabled={loading}
        />
        <Input
          label="Your name"
          id="reg-name"
          value={form.name}
          onChange={set('name')}
          required
          disabled={loading}
          autoComplete="name"
        />
        <div className="auth-field-row">
          <Input
            label="Email"
            id="reg-email"
            type="email"
            placeholder="you@firm.com"
            value={form.email}
            onChange={set('email')}
            required
            disabled={loading}
            autoComplete="email"
          />
          <Input
            label="Phone"
            id="reg-phone"
            value={form.phone}
            onChange={set('phone')}
            disabled={loading}
            autoComplete="tel"
          />
        </div>
        <div className="auth-field-row">
          <AuthPasswordInput
            label="Password"
            id="reg-password"
            value={form.password}
            onChange={set('password')}
            required
            disabled={loading}
            autoComplete="new-password"
          />
          <Input
            label="Confirm password"
            id="reg-confirm"
            type="password"
            value={form.confirm}
            onChange={set('confirm')}
            required
            disabled={loading}
            autoComplete="new-password"
          />
        </div>
        <p className="auth-hint">10+ characters with upper, lower, a digit and a special character.</p>

        <div className="auth-otp-row">
          <Input
            label="Email verification code"
            id="reg-otp"
            inputMode="numeric"
            placeholder="6-digit code"
            value={form.otp}
            // Filtered on the way in rather than validated on submit: the field
            // used to take thirty digits and only the server said no.
            onChange={(e) => setForm((f) => ({ ...f, otp: sanitizeOtp(e.target.value) }))}
            maxLength={OTP_LENGTH}
            required
            disabled={loading}
            autoComplete="one-time-code"
            helperText={otpSent ? 'Sent — it expires in 10 minutes.' : 'Send a code to confirm your email.'}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={sendOtp}
            loading={otpSending}
            disabled={loading}
          >
            {otpSent ? 'Resend' : 'Send code'}
          </Button>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          className="auth-submit"
          disabled={!isCompleteOtp(form.otp)}
        >
          {loading ? 'Creating your firm…' : 'Create Firm — Free'}
        </Button>
      </form>
      )}
    </AuthLayout>
  );
}

export default Register;
